import { useEffect, useMemo, useState } from 'react'
import { api } from '../api/client'
import type { ComplianceItem } from '../types'
import { daysBetween, fmtDate, fmtMoney, todayISO } from '../lib/format'
import Badge, { type BadgeColor } from '../components/Badge'

const CATEGORIES: ComplianceItem['category'][] = [
  'license',
  'insurance',
  'tax',
  'llc',
  'payroll',
  'education',
  'permit',
  'hoa',
  'other',
]

export default function ComplianceTab() {
  const [items, setItems] = useState<ComplianceItem[]>([])
  const [adding, setAdding] = useState(false)
  const [draft, setDraft] = useState<Partial<ComplianceItem>>({
    category: 'other',
    recurrence: 'once',
    due_date: todayISO(),
    reminder_days_before: '30,14,3',
  })
  const [filter, setFilter] = useState<'open' | 'all' | 'overdue' | 'soon'>('open')

  useEffect(() => {
    void api.compliance.list().then(setItems)
  }, [])

  const filtered = useMemo(() => {
    const now = new Date()
    return items.filter((i) => {
      if (filter === 'open') return i.completed === 0
      if (filter === 'overdue') return i.completed === 0 && new Date(i.due_date) < now
      if (filter === 'soon') {
        if (i.completed === 1) return false
        const days = daysBetween(now, i.due_date) ?? 999
        return days >= 0 && days <= 30
      }
      return true
    })
  }, [items, filter])

  async function add() {
    if (!draft.title || !draft.due_date || !draft.category) return
    const item = await api.compliance.create(draft)
    setItems((p) => [...p, item])
    setAdding(false)
    setDraft({
      category: 'other',
      recurrence: 'once',
      due_date: todayISO(),
      reminder_days_before: '30,14,3',
    })
  }

  async function toggleComplete(item: ComplianceItem) {
    if (
      !window.confirm(
        item.completed
          ? `Mark "${item.title}" as not complete?`
          : `Mark "${item.title}" as complete?\n\n${item.recurrence !== 'once' ? `Will auto-advance the due date by ${item.recurrence}.` : ''}`
      )
    )
      return
    const updated = await api.compliance.update(item.id, { completed: item.completed ? 0 : 1 })
    setItems((p) => p.map((x) => (x.id === item.id ? updated : x)))
  }

  async function remove(id: string) {
    if (!window.confirm('Delete this compliance item?')) return
    await api.compliance.delete(id)
    setItems((p) => p.filter((x) => x.id !== id))
  }

  return (
    <section>
      <div className="row-between mb-3">
        <div>
          <h2>Compliance Calendar</h2>
          <p className="muted small">
            {items.length} items · Heritage's recurring deadlines (license, insurance, tax,
            payroll, etc.)
          </p>
        </div>
        <div className="row gap-2">
          <select className="search" value={filter} onChange={(e) => setFilter(e.target.value as typeof filter)}>
            <option value="open">Open</option>
            <option value="overdue">Overdue</option>
            <option value="soon">Within 30 days</option>
            <option value="all">All</option>
          </select>
          <button className="btn-primary" onClick={() => setAdding(!adding)}>
            {adding ? 'Cancel' : '+ Item'}
          </button>
        </div>
      </div>

      <div className="callout small mb-3">
        <strong>Recurring items</strong> auto-advance their due date when you mark complete (annual,
        quarterly, monthly). One-time items just close out.
      </div>

      {adding && (
        <div className="card mb-3">
          <label className="form-row">
            <span>Title *</span>
            <input
              autoFocus
              value={draft.title ?? ''}
              onChange={(e) => setDraft({ ...draft, title: e.target.value })}
              placeholder="Builder license renewal"
            />
          </label>
          <label className="form-row">
            <span>Category</span>
            <select
              value={draft.category}
              onChange={(e) => setDraft({ ...draft, category: e.target.value as ComplianceItem['category'] })}
            >
              {CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </label>
          <label className="form-row">
            <span>Due date *</span>
            <input
              type="date"
              value={draft.due_date}
              onChange={(e) => setDraft({ ...draft, due_date: e.target.value })}
            />
          </label>
          <label className="form-row">
            <span>Recurrence</span>
            <select
              value={draft.recurrence}
              onChange={(e) => setDraft({ ...draft, recurrence: e.target.value as ComplianceItem['recurrence'] })}
            >
              <option value="once">once</option>
              <option value="annual">annual</option>
              <option value="quarterly">quarterly</option>
              <option value="monthly">monthly</option>
            </select>
          </label>
          <label className="form-row">
            <span>Responsible</span>
            <input
              value={draft.responsible ?? ''}
              onChange={(e) => setDraft({ ...draft, responsible: e.target.value })}
              placeholder="Dustin / CPA / Gusto"
            />
          </label>
          <label className="form-row">
            <span>Cost ($)</span>
            <input
              type="number"
              value={(draft.cost as number | undefined) ?? ''}
              onChange={(e) => setDraft({ ...draft, cost: parseFloat(e.target.value) || null })}
            />
          </label>
          <label className="form-row">
            <span>External URL</span>
            <input
              type="url"
              value={draft.external_url ?? ''}
              onChange={(e) => setDraft({ ...draft, external_url: e.target.value })}
              placeholder="https://..."
            />
          </label>
          <label className="form-row">
            <span>Notes</span>
            <textarea
              rows={2}
              value={draft.notes ?? ''}
              onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
            />
          </label>
          <button className="btn-primary" onClick={add}>
            Add deadline
          </button>
        </div>
      )}

      {filtered.length === 0 && <p className="muted">No items match this filter.</p>}

      {filtered.map((item) => {
        const days = daysBetween(new Date(), item.due_date) ?? 999
        const overdue = !item.completed && days < 0
        const soon = !item.completed && days >= 0 && days <= 30
        let color: BadgeColor = 'gray'
        if (overdue) color = 'red'
        else if (soon) color = 'gold'
        else if (item.completed) color = 'green'
        return (
          <div key={item.id} className="card mb-2">
            <div className="row-between">
              <div className="grow">
                <div className="row-title">
                  <span style={{ textDecoration: item.completed ? 'line-through' : 'none' }}>
                    {item.title}
                  </span>
                </div>
                <div className="muted small">
                  {item.category}
                  {item.responsible && ` · ${item.responsible}`}
                  {item.recurrence !== 'once' && ` · ${item.recurrence}`}
                  {item.cost !== null && ` · ~${fmtMoney(item.cost)}`}
                </div>
                {item.description && <div className="small mt-1">{item.description}</div>}
                {item.notes && <div className="muted small mt-1">{item.notes}</div>}
                {item.external_url && (
                  <div className="mt-1">
                    <a href={item.external_url} target="_blank" rel="noopener noreferrer">
                      Open portal →
                    </a>
                  </div>
                )}
              </div>
              <div className="text-right">
                <div className="row-date">{fmtDate(item.due_date)}</div>
                <Badge color={color}>
                  {item.completed
                    ? 'COMPLETE'
                    : overdue
                      ? `${Math.abs(days)}d LATE`
                      : `${days}d`}
                </Badge>
              </div>
            </div>
            <div className="row gap-2 mt-2">
              <button className="btn-ghost small" onClick={() => toggleComplete(item)}>
                {item.completed ? 'Unmark' : 'Mark complete'}
              </button>
              <button className="btn-ghost small" onClick={() => remove(item.id)}>
                ✕ Delete
              </button>
            </div>
          </div>
        )
      })}
    </section>
  )
}
